// SUPPORT1 (docs/build-briefs/SUPPORT1-get-help-and-support.md; mockups design/mockups/entbasics/support1-get-help-and-support.html).
// The pure part of Get help and Snip & Sip Admin's Support section: the shapes the server answers with, the status words and
// chips, the age and time text, and the form's own checks. No DOM, no network, so the words are decided in one place.

export type SupportType = "problem" | "question";
export type CustomerStatus = "waiting_on_support" | "waiting_on_you" | "closed";
export type StaffStatus = "new" | "needs_reply" | "waiting_on_customer" | "closed";

export interface CustomerRequestSummaryDto {
  id: string;
  number: string;
  requestType: SupportType;
  title: string;
  status: CustomerStatus;
  createdAt: string;
  lastActivityAt: string;
  lastStaffReplyAt: string | null;
  hasNewReply: boolean;
}

export interface CustomerMessageDto {
  id: string;
  from: "you" | "support";
  authorLabel: string;
  channel: "app" | "email";
  body: string;
  createdAt: string;
}

export interface CustomerRequestDetailDto extends CustomerRequestSummaryDto {
  errorReference: string | null;
  screenRoute: string;
  screenLabel: string;
  contactEmail: string;
  messages: CustomerMessageDto[];
}

export interface StaffRequestSummaryDto {
  id: string;
  number: string;
  status: StaffStatus;
  requestType: SupportType;
  title: string;
  customerName: string;
  locationName: string | null;
  requesterName: string;
  console: "location" | "franchisor" | "admin";
  errorReference: string | null;
  createdAt: string;
  lastReply: { by: "customer" | "support"; at: string } | null;
}

export interface StaffMessageDto {
  id: string;
  kind: "customer" | "staff" | "internal_note" | "status_change";
  authorLabel: string;
  channel: "app" | "email";
  body: string;
  createdAt: string;
}

export interface StaffRequestDetailDto extends StaffRequestSummaryDto {
  contactEmail: string;
  screenRoute: string;
  screenLabel: string;
  buildRevision: string;
  messages: StaffMessageDto[];
}

export const BODY_MAX = 2000;
export const TITLE_MAX = 80;
export const CODE_PATTERN = /^[0-9A-HJKMNP-TV-Z]{4}-[0-9A-HJKMNP-TV-Z]{4}$/;
export const SUPPORT_EMAIL_SHAPE = /^[^@\s<>"']+@[^@\s<>"']+\.[^@\s<>"']+$/;

export type ChipTone = "good" | "info" | "bad" | "warn" | "neutral";
export interface Chip {
  readonly symbol: string;
  readonly words: string;
  readonly tone: ChipTone;
}

/** What the person reads. "Waiting on you" is the opposite of the Admin's "Needs reply". */
export function customerChip(status: CustomerStatus): Chip {
  switch (status) {
    case "waiting_on_you":
      return { symbol: "!", words: "Waiting on you", tone: "warn" };
    case "waiting_on_support":
      return { symbol: "", words: "Waiting on Snip & Sip", tone: "info" };
    case "closed":
      return { symbol: "✓", words: "Closed", tone: "good" };
  }
}

export function staffChip(status: StaffStatus): Chip {
  switch (status) {
    case "new":
      return { symbol: "!", words: "New", tone: "warn" };
    case "needs_reply":
      return { symbol: "!", words: "Needs reply", tone: "warn" };
    case "waiting_on_customer":
      return { symbol: "", words: "Waiting on customer", tone: "info" };
    case "closed":
      return { symbol: "✓", words: "Closed", tone: "good" };
  }
}

export const STAFF_STATUS_LABELS: Readonly<Record<StaffStatus, string>> = {
  new: "New",
  needs_reply: "Needs reply",
  waiting_on_customer: "Waiting on customer",
  closed: "Closed",
};

export function typeWord(type: SupportType): string {
  return type === "problem" ? "Problem" : "Question";
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function clock(date: Date): string {
  const hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${hours % 12 === 0 ? 12 : hours % 12}:${minutes} ${hours < 12 ? "AM" : "PM"}`;
}

/** "Oct 22, 2:14 PM" in the viewer's own time. */
export function formatWhen(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return `${MONTHS[date.getMonth()] ?? ""} ${date.getDate()}, ${clock(date)}`;
}

/** "Oct 22" for the list. */
export function formatDay(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return `${MONTHS[date.getMonth()] ?? ""} ${date.getDate()}`;
}

/** "Reply today, 2:31 PM" or "Oct 9" for the customer's list row. */
export function listWhen(iso: string, hasReply: boolean, now: Date): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const sameDay = date.toDateString() === now.toDateString();
  if (hasReply) return sameDay ? `Reply today, ${clock(date)}` : `Reply ${formatDay(iso)}`;
  return sameDay ? `Today, ${clock(date)}` : formatDay(iso);
}

/** "12 min", "1 hr", "3 days": how long ago, rounded down, never shorter than a minute. */
export function ageText(iso: string, now: Date): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const minutes = Math.max(1, Math.floor((now.getTime() - then) / 60_000));
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hr`;
  const days = Math.floor(hours / 24);
  return days === 1 ? "1 day" : `${days} days`;
}

/** "Support, 6 hr ago". */
export function lastReplyText(reply: StaffRequestSummaryDto["lastReply"], now: Date): string {
  if (reply === null) return "";
  return `${reply.by === "support" ? "Support" : "Customer"}, ${ageText(reply.at, now)} ago`;
}

export function countLabel(length: number): string {
  return `${length.toLocaleString("en-US")} of ${BODY_MAX.toLocaleString("en-US")}`;
}

export function newReplyWords(count: number): string {
  return count === 1 ? "1 new reply" : `${count} new replies`;
}

export interface ReportFields {
  readonly title: string;
  readonly body: string;
  readonly email: string;
}

export interface ReportErrors {
  title?: string;
  body?: string;
  email?: string;
}

/** Checked after Send is pressed, never while typing (style guide section 7). */
export function reportErrors(fields: ReportFields): ReportErrors {
  const errors: ReportErrors = {};
  if (fields.title.trim() === "") errors.title = "Enter a short title.";
  if (fields.body.trim() === "") errors.body = "Tell us what happened.";
  else if (fields.body.length > BODY_MAX) errors.body = "Keep it under 2,000 characters.";
  if (!SUPPORT_EMAIL_SHAPE.test(fields.email.trim())) errors.email = "Enter an email address we can reply to.";
  return errors;
}

/** A sentence for a refused or failed call. Server text is used only for the two refusals written for a person. */
export function failureSentence(status: number, serverMessage: string | undefined): string {
  if (status === 429) return serverMessage ?? "You have sent a lot of messages in the last hour. Please wait a little and try again.";
  if (status === 403) return "Your sign-in cannot send help requests.";
  if (status === 400 && serverMessage !== undefined && !/request body|is not accepted|must be/i.test(serverMessage)) return serverMessage;
  return "That did not send. Check your connection and try again.";
}

/** The page name drawn from a heading, plain words only. */
export function pageLabelFrom(heading: string | null | undefined, fallback: string): string {
  const text = (heading ?? "").replace(/\s+/g, " ").trim();
  return (text === "" ? fallback : text).slice(0, 80);
}

/** The route sent with a request: the page path plus an in-page "#/" route, never a query or a typed value. */
export function routeFrom(pathname: string, hash: string): string {
  const inPage = hash.startsWith("#/") ? hash.slice(1) : "";
  const base = inPage === "" ? pathname : pathname.replace(/\/+$/, "");
  return `${base}${inPage}`.slice(0, 200);
}
